import { Link, createFileRoute } from "@tanstack/react-router";
import { ShoppingBag } from "lucide-react";
import { useShop, formatDH } from "@/lib/shop-store";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeading } from "@/components/SectionHeading";

export const Route = createFileRoute("/panier")({
  head: () => ({
    meta: [
      { title: "Panier — Sunset Concept Store" },
      {
        name: "description",
        content: "Votre panier Sunset Concept Store. Paiement à la livraison partout au Maroc.",
      },
      { property: "og:title", content: "Panier — Sunset Concept Store" },
      { property: "og:description", content: "Votre panier Sunset Concept Store." },
      { property: "og:url", content: "https://sunsetconcept.store/panier" },
    ],
    links: [{ rel: "canonical", href: "https://sunsetconcept.store/panier" }],
  }),
  component: PanierPage,
});

function PanierPage() {
  const { cart, cartTotal, checkout, checkingOut, checkoutError } = useShop();

  return (
    <main className="mx-auto min-h-[70vh] max-w-3xl px-6 py-24 md:py-32">
      <SectionHeading
        eyebrow="Commande"
        title="Votre panier"
        subtitle="Paiement à la livraison disponible partout au Maroc."
      />

      {/* Espace réservé à hauteur fixe pour les articles, empêche tout saut au chargement */}
      <div className="mt-12 min-h-[18rem] rounded-sm border border-border bg-card/40 p-6">
        {cart.length === 0 ? (
          <div className="flex h-full min-h-[16rem] flex-col items-center justify-center text-center">
            <div className="mb-6 grid h-16 w-16 place-items-center rounded-full bg-secondary">
              <ShoppingBag size={24} strokeWidth={1.2} className="text-muted-foreground" />
            </div>
            <p className="font-serif text-2xl">Votre panier est vide</p>
            <p className="label-xs mt-3 max-w-xs text-muted-foreground">
              Explorez nos nouveautés et ajoutez vos pièces préférées.
            </p>
            <Link
              to="/"
              className="label-xs mt-8 border border-foreground px-8 py-4 transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              Découvrir la collection
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {cart.map((line) => (
              <li key={line.id} className="flex h-28 items-center gap-4 py-4">
                <Skeleton className="h-20 w-16 shrink-0 rounded-sm" />
                <div className="flex h-20 min-w-0 flex-1 flex-col justify-between">
                  <div>
                    <p className="line-clamp-2 font-serif text-lg leading-snug">{line.name}</p>
                    <p className="label-xs mt-1 text-muted-foreground">Qté {line.qty}</p>
                  </div>
                  <p className="text-sm tabular-nums">{formatDH(line.price * line.qty)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Résumé de commande avec hauteur fixe */}
      <div className="mt-4 flex h-16 items-center justify-between rounded-sm border border-border px-6">
        <span className="label-xs text-muted-foreground">Sous-total</span>
        <span className="text-sm tabular-nums">{formatDH(cartTotal)}</span>
      </div>

      <button
        type="button"
        disabled={cart.length === 0 || checkingOut}
        onClick={() => void checkout()}
        className="btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-40"
      >
        {checkingOut ? "Redirection vers le paiement…" : "Passer la commande"}
      </button>
      {checkoutError ? (
        <p role="alert" className="mt-3 text-center text-sm text-destructive">
          {checkoutError}
        </p>
      ) : null}
    </main>
  );
}
